/**
 * The only place a date string is parsed.
 *
 * The server sends ISO strings. An earlier bug in the command view came from
 * comparing a parsed Date against a date string and getting false for every
 * row — silently emptying the overdue list. So parsing lives here, comparison
 * happens on days rather than instants, and nothing else in the UI calls
 * `new Date` on server data.
 */

const DAY = /^\d{4}-\d{2}-\d{2}$/;

function parse(value: string): Date | null {
  // A bare date is midnight *local*, not UTC: treating "2026-10-04" as UTC
  // shifts it a day backwards for anyone west of Greenwich, which is exactly
  // the kind of error that makes a due date look overdue when it is not.
  const iso = DAY.test(value) ? `${value}T00:00:00` : value;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const dayFormat = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const dayShortFormat = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
});

const timeFormat = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** "4 Oct 2026". Em dash when absent, never an empty cell. */
export function day(value: string | null | undefined): string {
  if (!value) return "—";
  const d = parse(value);
  return d ? dayFormat.format(d) : "—";
}

/** "4 Oct" — for dense columns where the year is implied by context. */
export function dayShort(value: string | null | undefined): string {
  if (!value) return "—";
  const d = parse(value);
  return d ? dayShortFormat.format(d) : "—";
}

/** "4 Oct · 14:32" — for register entries, where the hour matters. */
export function stamp(value: string | null | undefined): string {
  if (!value) return "—";
  const d = parse(value);
  if (!d) return "—";
  return `${dayShortFormat.format(d)} · ${timeFormat.format(d)}`;
}

/** Whole days from today. Negative is in the past. */
export function daysFromToday(value: string | null | undefined): number | null {
  if (!value) return null;
  const d = parse(value);
  if (!d) return null;

  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const diff = start(d).getTime() - start(new Date()).getTime();
  return Math.round(diff / 86_400_000);
}

/**
 * "today", "tomorrow", "in 4 days", "6 days ago".
 *
 * Deliberately plain. A due date is the thing a Lead scans for, and "overdue by
 * 6 days" reads faster than a date they have to subtract in their head.
 */
export function relativeDay(value: string | null | undefined): string {
  const n = daysFromToday(value);
  if (n === null) return "—";
  if (n === 0) return "today";
  if (n === 1) return "tomorrow";
  if (n === -1) return "yesterday";
  if (n > 1) return `in ${n} days`;
  return `${Math.abs(n)} days ago`;
}

/** Sentence case from an enum value: `in_progress` → `In progress`. */
export function humanise(value: string): string {
  const spaced = value.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** `04_draft_deliverables` → `Draft deliverables`, keeping the number aside. */
export function folderLabel(key: string): { ordinal: string; name: string } {
  const match = /^(\d+)_(.*)$/.exec(key);
  if (!match) return { ordinal: "", name: humanise(key) };
  return { ordinal: match[1]!, name: humanise(match[2]!) };
}

/** A classification as the matrix words it. */
export function classificationLabel(value: string): string {
  switch (value) {
    case "client_visible":
      return "Client visible";
    case "internal":
      return "Internal";
    case "restricted":
      return "Restricted";
    case "highly_restricted":
      return "Highly restricted";
    default:
      return humanise(value);
  }
}

/** Shortens a uuid for display without pretending it is the whole thing. */
export function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

export function initials(fullName: string): string {
  return fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join("");
}

/**
 * Minor units to something a person reads.
 *
 * The only division by 100 in the codebase. Everything upstream — the
 * database columns, the API, the forms — carries whole kobo, because a
 * float cannot represent 0.10 and a bill that is a penny out is a bill
 * somebody has to explain.
 */
export function money(minorUnits: number, currency = "NGN"): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(minorUnits / 100);
  } catch {
    // An unknown or malformed currency code must not take the page down.
    return `${currency} ${(minorUnits / 100).toFixed(2)}`;
  }
}
